/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useTranslation } from 'react-i18next';
import { ActionIcon, Center } from '@mantine/core';
import { IconStar, IconStarFilled } from '@tabler/icons-react';
import {
  ResourceRowFragmentFragment,
  useUpdateFavoriteResourcesMutation,
} from '@app/types/blackroom-api';
import { CellProps } from '../types';
import { ICON_SIZE_MAP } from '@app/shared/ui-kit/icons-sizes';
import { palette } from '@app/shared/ui-kit/colors';
import { ResourceScope } from '@app/types/blackroom-app';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function CellFavorite({
  row,
  scope,
}: CellProps<ResourceRowFragmentFragment> & {
  scope?: ResourceScope;
}) {
  /* ***************************** LOCAL VARIABLES **************************** */

  const { t } = useTranslation(['Common']);
  const resource = row.original;
  const Icon = resource.isFavorite ? IconStarFilled : IconStar;

  // API
  const [updateFavoriteResources] = useUpdateFavoriteResourcesMutation();

  /* ******************************** RENDERING ******************************* */
  return (
    <Center w="40%">
      <ActionIcon
        variant="transparent"
        color="blackroomGray"
        aria-label={t('Common:actions.toggleFavorite')}
        onClick={async () => {
          await updateFavoriteResources({
            variables: {
              updateFavoriteResourcesInput: {
                resources: [
                  { id: resource.id, isFavorite: !resource.isFavorite },
                ],
              },
            },
            optimisticResponse: {
              __typename: 'Mutation',
              updateFavoriteResources: [
                {
                  ...resource,
                  __typename: 'Resource',
                  isFavorite: !resource.isFavorite,
                  updatedAt: new Date().toDateString(),
                },
              ],
            },
            update: (cache, { data }) => {
              const resourceUpdated = data?.updateFavoriteResources.find(
                (r) => r.id === resource.id,
              );

              if (resourceUpdated?.isFavorite || scope !== 'favorites') return;

              cache.modify({
                fields: {
                  resources: (existing = { nodes: [] }, { readField }) => {
                    const filteredNodes = existing.nodes.filter((ref: any) => {
                      const id = readField('id', ref);
                      return id !== resourceUpdated?.id;
                    });
                    return {
                      ...existing,
                      nodes: filteredNodes,
                    };
                  },
                },
              });
            },
          });
        }}
      >
        <Icon
          color={
            resource.isFavorite
              ? palette.blackroomYellow
              : palette.blackroomGray
          }
          size={ICON_SIZE_MAP.smaller}
          cursor="pointer"
        />
      </ActionIcon>
    </Center>
  );
}

export default CellFavorite;
